import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Map, Pencil, Droplets, Save, Send, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import MapStyleSwitcher from "../components/MapStyleSwitcher";
import { useChangeOrderMap, calcAcres } from "./ChangeOrder/hooks/useChangeOrderMap";
import { COLineItemsCard } from "./ChangeOrder/COLineItemsCard";

const ChangeOrderBuilderPage = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [lineItems, setLineItems] = useState([
    { id: Date.now().toString(), service_name: "", description: "", quantity: 1, unit: "", unit_price: 0, total: 0 },
  ]);
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentDescription, setAdjustmentDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [addToBalance, setAddToBalance] = useState(false);

  const {
    mapContainerRef, mapRef, isDrawing, polygons, showWetlands,
    totalAcres, startDrawing, cancelDrawing, removePolygon,
    toggleWetlands, reloadCustomLayers,
  } = useChangeOrderMap(quote);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await axios.get(`${API}/quotes/${quoteId}`);
        setQuote(response.data);
      } catch {
        toast.error("Quote not found");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId, navigate]);

  const addLineItem = (sectionHeaderId = null) => {
    const newItem = { id: Date.now().toString() + Math.random().toString(36).slice(2, 6), service_name: "", description: "", quantity: 1, unit: "", unit_price: 0, total: 0 };
    setLineItems((prev) => {
      if (!sectionHeaderId) return [...prev, newItem];
      const headerIdx = prev.findIndex((i) => i.id === sectionHeaderId);
      if (headerIdx === -1) return [...prev, newItem];
      let insertIdx = prev.length;
      for (let i = headerIdx + 1; i < prev.length; i++) {
        if (prev[i].is_section_header) { insertIdx = i; break; }
      }
      const next = [...prev];
      next.splice(insertIdx, 0, newItem);
      return next;
    });
  };

  const addSection = () => {
    setLineItems((prev) => [
      ...prev,
      { id: Date.now().toString() + Math.random().toString(36).slice(2, 6), is_section_header: true, service_name: "New Section", description: "", quantity: 0, unit: "", unit_price: 0, total: 0 },
    ]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        if (field === "quantity" || field === "unit_price")
          updated.total = (updated.quantity || 0) * (updated.unit_price || 0);
        return updated;
      })
    );
  };

  const removeLineItem = (id) => setLineItems((prev) => prev.filter((item) => item.id !== id));

  const totalAmount = lineItems.reduce((sum, item) => sum + (item.total || 0), 0) + (parseFloat(adjustmentAmount) || 0);

  const handleSave = async (sendAfterSave = false) => {
    setSaving(true);
    try {
      const payload = {
        quote_id: quoteId,
        line_items: lineItems.filter((item) => item.is_section_header || item.service_name),
        adjustment_amount: parseFloat(adjustmentAmount) || 0,
        adjustment_description: adjustmentDescription,
        notes,
        requires_approval: requiresApproval,
        billing_mode: addToBalance ? "quote_balance" : "separate",
        map_areas: polygons.length > 0 ? polygons : null,
      };
      const response = await axios.post(`${API}/quotes/${quoteId}/change-orders`, payload);
      if (sendAfterSave) {
        await axios.post(`${API}/change-orders/${response.data.id}/send`, {}, { headers: { origin: window.location.origin } });
        toast.success("Change order created and sent to customer!");
      } else {
        toast.success("Change order saved as draft!");
      }
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create change order");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00341d]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="change-order-builder">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")} className="h-9 px-3">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold uppercase tracking-tight text-[#00341d]">
                New Change Order
              </h1>
              <p className="text-sm text-zinc-500">
                {quote?.quote_number} &mdash; {quote?.customer_name}
                {quote?.property_address && <span className="ml-2 text-zinc-400">| {quote.property_address}</span>}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save Draft"}
            </Button>
            <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#00341d] hover:bg-[#002816] text-white" data-testid="save-send-btn">
              <Send className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save & Send"}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Map Section */}
        <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
          <CardHeader className="border-b border-zinc-100 px-6 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg uppercase tracking-tight text-[#00341d] flex items-center gap-2">
                <Map className="w-5 h-5" /> Change Order Area
              </CardTitle>
              <div className="flex items-center gap-3">
                {totalAcres > 0 && (
                  <div className="bg-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-bold">
                    {totalAcres.toFixed(2)} acres
                  </div>
                )}
                {polygons.length > 0 && (
                  <span className="text-sm text-zinc-500">{polygons.length} area{polygons.length > 1 ? "s" : ""}</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="relative">
              <div ref={mapContainerRef} className="h-[450px] w-full" data-testid="co-map" />
              <div className="absolute bottom-4 left-4 flex gap-2 z-10">
                {!isDrawing ? (
                  <Button onClick={startDrawing} className="bg-orange-500 hover:bg-orange-600 text-white shadow-lg" data-testid="co-draw-area-btn">
                    <Pencil className="w-4 h-4 mr-2" /> Draw Area
                  </Button>
                ) : (
                  <Button onClick={cancelDrawing} variant="outline" className="bg-white shadow-lg">
                    Cancel Drawing
                  </Button>
                )}
                <Button
                  onClick={toggleWetlands}
                  variant="outline"
                  className={`shadow-lg ${showWetlands ? "bg-cyan-600 text-white hover:bg-cyan-700 border-cyan-600" : "bg-white"}`}
                >
                  <Droplets className="w-4 h-4 mr-2" /> Wetlands
                </Button>
              </div>
              <div className="absolute bottom-4 right-4 z-10">
                <MapStyleSwitcher mapRef={mapRef} onStyleLoad={reloadCustomLayers} />
              </div>
              {(quote?.polygons?.length > 0 || polygons.length > 0) && (
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-lg p-3 max-w-xs z-10">
                  {quote?.polygons?.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Original Quote Areas</p>
                      {quote.polygons.map((poly, i) => (
                        <div key={`orig-${i}`} className="flex items-center justify-between py-0.5 text-xs">
                          <span className="text-blue-600 flex items-center gap-1">
                            <span className="w-2 h-0.5 bg-blue-500 inline-block" /> Area {i + 1}
                          </span>
                          <span className="text-blue-600 font-medium">{calcAcres(poly).toFixed(2)} ac</span>
                        </div>
                      ))}
                      {polygons.length > 0 && <div className="border-t border-zinc-200 my-1.5" />}
                    </>
                  )}
                  {polygons.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1">Change Order Areas</p>
                      {polygons.map((poly, i) => (
                        <div key={`co-${i}`} className="flex items-center justify-between py-0.5 text-xs group">
                          <span className="text-orange-600 flex items-center gap-1">
                            <span className="w-2 h-0.5 bg-orange-500 inline-block" /> Area {i + 1}
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-orange-600 font-medium">{calcAcres(poly).toFixed(2)} ac</span>
                            <button
                              onClick={() => removePolygon(i)}
                              className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-0.5 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-zinc-200 mt-1.5 pt-1.5 flex justify-between text-xs font-bold text-orange-700">
                        <span>CO Total</span>
                        <span>{totalAcres.toFixed(2)} ac</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Line Items — extracted component */}
        <COLineItemsCard
          lineItems={lineItems}
          onAdd={addLineItem}
          onAddSection={addSection}
          onUpdate={updateLineItem}
          onRemove={removeLineItem}
        />

        {/* Adjustment + Notes */}
        <div className="grid grid-cols-2 gap-6">
          <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
            <CardHeader className="border-b border-zinc-100 px-6 py-4">
              <CardTitle className="font-heading text-lg uppercase tracking-tight text-[#00341d]">Adjustment</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Price Adjustment (+/-)</Label>
                <Input type="number" value={adjustmentAmount} onChange={(e) => setAdjustmentAmount(e.target.value)} placeholder="0.00" />
                <p className="text-xs text-zinc-400 mt-1">Use negative for discount/credit</p>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Description</Label>
                <Input value={adjustmentDescription} onChange={(e) => setAdjustmentDescription(e.target.value)} placeholder="e.g., Scope reduction credit" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
            <CardHeader className="border-b border-zinc-100 px-6 py-4">
              <CardTitle className="font-heading text-lg uppercase tracking-tight text-[#00341d]">Notes</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border rounded-md p-3 text-sm min-h-[100px] focus:ring-2 focus:ring-[#00341d]/20 focus:border-[#00341d] outline-none"
                placeholder="Describe the changes..."
              />
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="checkbox"
                  id="co-requires-approval"
                  checked={requiresApproval}
                  onChange={(e) => setRequiresApproval(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="co-requires-approval" className="text-sm cursor-pointer">
                  Require customer approval
                </Label>
              </div>
              <div className="flex items-start gap-2 mt-3 pt-3 border-t border-zinc-100">
                <input
                  type="checkbox"
                  id="co-add-to-balance"
                  checked={addToBalance}
                  onChange={(e) => setAddToBalance(e.target.checked)}
                  className="rounded mt-0.5"
                  data-testid="co-builder-add-to-balance"
                />
                <Label htmlFor="co-add-to-balance" className="text-sm cursor-pointer">
                  Add to quote balance (include in the invoice)
                  <span className="block text-xs text-zinc-400 font-normal mt-0.5">
                    Saves it onto the quote's balance due. Use <strong>Save Draft</strong> to save without emailing the customer a change-order notice.
                  </span>
                </Label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary footer */}
        <Card className="bg-[#00341d] text-white border-0 shadow-lg rounded-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/60 uppercase tracking-wider mb-1">Change Order Total</p>
                <p className="text-3xl font-bold">${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                {totalAcres > 0 && <p className="text-sm text-white/60 mt-1">{totalAcres.toFixed(2)} acres mapped</p>}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="border-white/30 text-white hover:bg-white/10">
                  <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save Draft"}
                </Button>
                <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#d97706] hover:bg-[#b45309] text-white">
                  <Send className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save & Send"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChangeOrderBuilderPage;
